import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { collection, getDocs, addDoc, query, orderBy, writeBatch, serverTimestamp, getDoc, doc } from 'firebase/firestore';
import { Heart, CheckCircle, X as XIcon, ArrowClockwise, ArrowRight, Trophy, Medal, Book, Download, Smiley, ChartPie } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import ReviewMistakes from '../components/ReviewMistakes';
import ReactConfetti from 'react-confetti';
import html2canvas from 'html2canvas';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

// 점수에 따른 메시지와 아이콘을 반환하는 함수
const getScoreMessage = (percentage) => {
  if (percentage === 100) return { message: "완벽해요! 대단해요! 🌟", icon: <Trophy className="text-yellow-400" /> };
  if (percentage >= 80) return { message: "정말 잘했어요! 👏", icon: <Medal className="text-blue-400" /> };
  if (percentage >= 60) return { message: "좋은 성과예요! 😊", icon: <Smiley className="text-green-400" /> };
  return { message: "다음에는 더 잘할 수 있어요! 💪", icon: <ChartPie className="text-purple-400" /> };
};

const DisplayReportCard = ({ nickname, score, totalQuestions, onDownload }) => {
  const percentage = Math.round((score / totalQuestions) * 100);
  const { message } = getScoreMessage(percentage);

  const chartData = {
    labels: ['정답', '오답'],
    datasets: [{
      data: [score, totalQuestions - score],
      backgroundColor: ['#4CAF50', '#ff6b6b'],
      borderColor: ['#43A047', '#e74c3c'],
      borderWidth: 1,
    }],
  };

  const chartOptions = {
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  return (
    <div className="mb-8 pt-14">
      <div className="bg-yellow-50 rounded-xl shadow-lg p-6 relative overflow-hidden">
        <Button
          onClick={onDownload}
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4"
          title="레포트 카드 저장하기"
        >
          <Download size={16} />
        </Button>

        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          ✅{nickname}님의 퀴즈 풀이 결과
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
            <p className="text-sm text-gray-600 mb-2 font-semibold">총점</p>
            <p className="text-3xl font-bold text-blue-600">{score}/{totalQuestions}</p>
          </div>

          <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
            <p className="text-sm text-gray-600 mb-2 font-semibold">정답률</p>
            <p className="text-3xl font-bold text-green-600">{percentage}%</p>
          </div>

          <div className="h-[150px] relative">
            <Pie data={chartData} options={chartOptions} />
          </div>
        </div>

        <div className="mt-6 text-center bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg">
          <div className="flex items-center justify-center gap-2 text-xl font-semibold text-gray-700">
            <span>{message}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const DownloadReportCard = ({ nickname, score, totalQuestions }) => {
  const percentage = Math.round((score / totalQuestions) * 100);
  const { message } = getScoreMessage(percentage);

  const chartData = {
    labels: ['정답', '오답'],
    datasets: [{
      data: [score, totalQuestions - score],
      backgroundColor: ['#4CAF50', '#ff6b6b'],
      borderColor: ['#43A047', '#e74c3c'],
      borderWidth: 1,
    }],
  };

  const chartOptions = {
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          font: {
            size: 14,
            family: 'Arial'
          },
          padding: 20
        }
      }
    },
    responsive: true,
    maintainAspectRatio: true,
    layout: {
      padding: {
        top: 20,
        bottom: 20
      }
    },
    animation: {
      duration: 0 // 애니메이션 비활성화
    }
  };

  return (
    <div className="w-[800px] h-[400px] bg-white pt-2 pb-6 pl-6 pr-6 rounded-xl shadow-lg" style={{ background: 'linear-gradient(135deg, #fff 0%, #f3f4f6 100%)' }}>
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
        {nickname}님의 퀴즈 풀이 결과
      </h2>

      <div className="grid grid-cols-3 gap-8 items-center">
        <div className="text-center pt-1 pb-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
          <p className="text-lg text-gray-600 mb-1">총점</p>
          <p className="text-4xl font-bold text-blue-600 mb-2">{score}/{totalQuestions}</p>
        </div>

        <div className="text-center pt-2 pb-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
          <p className="text-lg text-gray-600 mb-1">정답률</p>
          <p className="text-4xl font-bold text-green-600 mb-2">{percentage}%</p>
        </div>

        <div className="h-[200px] w-[200px] mx-auto relative">
          <Pie data={chartData} options={chartOptions} />
        </div>
      </div>

      <div className="mt-8 text-center bg-gradient-to-r from-purple-50 to-pink-50 pt-1 pb-7 rounded-xl">
        <div className="flex items-center justify-center gap-3 text-2xl font-semibold text-gray-700">
          <span>{message}</span>
        </div>
      </div>
    </div>
  );
};

function QuizSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [userAnswer, setUserAnswer] = useState('');
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [nickname, setNickname] = useState('');
  const [showNicknameModal, setShowNicknameModal] = useState(true);
  const [results, setResults] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [showFlipModal, setShowFlipModal] = useState(false);
  const [showNextButton, setShowNextButton] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [isTeacher, setIsTeacher] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showNoMistakesModal, setShowNoMistakesModal] = useState(false);
  const cardRef = useRef(null);
  const [isRandomOrder, setIsRandomOrder] = useState(false);
  const [questionOrder, setQuestionOrder] = useState([]);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isExtremeMode, setIsExtremeMode] = useState(false);
  const [showExtremeModeWarning, setShowExtremeModeWarning] = useState(false);
  const downloadCardRef = useRef(null);
  const [showDownloadCard, setShowDownloadCard] = useState(false);

  const fetchQuestions = useCallback(async () => {
    const quizRef = doc(db, 'quizzes', id);
    const quizDoc = await getDoc(quizRef);
    if (quizDoc.exists()) {
      setIsRandomOrder(quizDoc.data().isRandomOrder || false);
      setIsExtremeMode(quizDoc.data().isExtremeMode || false);
    }

    const q = query(collection(db, `quizzes/${id}/questions`), orderBy("order", "asc"));
    const querySnapshot = await getDocs(q);
    let fetchedQuestions = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    console.log('Original questions:', fetchedQuestions);

    let order = fetchedQuestions.map((_, index) => index);
    if (isRandomOrder) {
      order = order.sort(() => Math.random() - 0.5);
    }
    setQuestionOrder(order);

    console.log('Question order:', order);
    setQuestions(fetchedQuestions);

    if (fetchedQuestions.length === 0) {
      setShowErrorModal(true);
    }
  }, [id, isRandomOrder]);

  useEffect(() => {
    console.log('isRandomOrder:', isRandomOrder);
    fetchQuestions();
  }, [fetchQuestions, isRandomOrder]);

  useEffect(() => {
    const checkIfTeacher = async () => {
      const user = auth.currentUser;
      if (user) {
        const quizDoc = await getDoc(doc(db, 'quizzes', id));
        if (quizDoc.exists() && quizDoc.data().userId === user.uid) {
          setIsTeacher(true);
        }
      }
    };
    checkIfTeacher();
  }, [id]);

  const handleNicknameSubmit = (e) => {
    e.preventDefault();
    if (nickname.trim()) {
      setShowNicknameModal(false);
    }
  };

  const handleCardFlip = () => {
    if (isExtremeMode) {
      setShowExtremeModeWarning(true);
    } else if (!isFlipped && !isAnswered) {
      setShowFlipModal(true);
    } else {
      setIsFlipped(!isFlipped);
    }
  };

  const confirmFlip = () => {
    const currentQ = getCurrentQuestion();
    setIsFlipped(true);
    setShowFlipModal(false);
    setResults([...results, { question: currentQ.question, isCorrect: false }]);
    setFeedback({
      isCorrect: false,
      message: isExtremeMode ? "다음 문제로 넘어갑니다." :
        currentQ.isMultipleChoice ?
          `정답은 ${currentQ.correctAnswers.map(i => currentQ.answerOptions[i]).join(', ')} 입니다.` :
          `정답은 "${currentQ.answer}" 입니다.`
    });
    setShowNextButton(true);
    setIsAnswered(true);
  };

  const getCurrentQuestion = () => questions[questionOrder[currentQuestion]];

  const handleSubmit = () => {
    const currentQ = getCurrentQuestion();
    if (!currentQ) {
      console.error('현재 질문 정의되지 않았습니다.');
      return;
    }

    let isCorrect = false;
    if (currentQ.isMultipleChoice) {
      // 선다형 문제의 경우
      const correctAnswerIndices = currentQ.correctAnswers;
      isCorrect = JSON.stringify(selectedAnswers.sort()) === JSON.stringify(correctAnswerIndices.sort());
    } else {
      // 단답형 문제의 경우
      if (Array.isArray(currentQ.answerOptions)) {
        // 여러 정답이 허용되는 단답형 문제
        isCorrect = currentQ.answerOptions.some(answer =>
          userAnswer.toLowerCase() === answer.toLowerCase()
        );
      } else {
        // 기존 단답형 문제
        isCorrect = userAnswer.toLowerCase() === currentQ.answer.toLowerCase();
      }
    }

    if (isCorrect) {
      setScore(score + 1);
      setFeedback({ isCorrect: true, message: "정답입니다!" });
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    } else {
      setFeedback({
        isCorrect: false,
        message: isExtremeMode ? "오답입니다." :
          currentQ.isMultipleChoice ?
            `오답입니다. 정답은 ${currentQ.correctAnswers.map(i => currentQ.answerOptions[i]).join(', ')} 입니다.` :
            `오답입니다. 정답은 "${currentQ.answer}" 입니다.`
      });
    }
    setResults([...results, { question: currentQ.question, isCorrect }]);
    setShowNextButton(true);
    setIsAnswered(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // 폼 제출 방지
      handleSubmit();
    }
  };

  const nextQuestion = () => {
    setIsFlipped(false);
    setFeedback(null);
    setUserAnswer('');
    setSelectedAnswers([]);
    setShowNextButton(false);
    setIsAnswered(false);

    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        setShowResult(true);
        saveResult();
      }
    }, 300);
  };

  const saveResult = async () => {
    try {
      const resultRef = await addDoc(collection(db, `quizzes/${id}/results`), {
        nickname,
        score,
        totalQuestions: questions.length,
        results,
        timestamp: serverTimestamp()
      });
      console.log("Result saved with ID: ", resultRef.id);
    } catch (error) {
      console.error("Error saving result: ", error);
    }
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setScore(0);
    setResults([]);
    setShowResult(false);
    setIsFlipped(false);
    setUserAnswer('');
    setFeedback(null);
    fetchQuestions(); // 문제를 다시 불러옵니다.
  };

  const fetchLeaderboard = useCallback(async () => {
    const q = query(
      collection(db, `quizzes/${id}/results`),
      orderBy("score", "desc"),
      orderBy("timestamp", "asc")
    );
    const querySnapshot = await getDocs(q);
    const leaderboardData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setLeaderboard(leaderboardData);
  }, [id]);

  const resetLeaderboard = async () => {
    if (window.confirm("정말로 리더보드를 초기화하시겠습니까?")) {
      const q = query(collection(db, `quizzes/${id}/results`));
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);
      querySnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      setLeaderboard([]);
    }
  };

  // 틀린 문제만 필터링하는 함수
  const getIncorrectQuestions = () => {
    const incorrectQuestions = results
      .filter(result => !result.isCorrect)
      .map(result => {
        const questionObj = questions.find(q => q.question === result.question);
        return questionObj ? {
          question: questionObj.question,
          answer: questionObj.answer,
          isMultipleChoice: questionObj.isMultipleChoice,
          answerOptions: questionObj.answerOptions,
          correctAnswers: questionObj.correctAnswers
        } : null;
      })
      .filter(q => q !== null);

    if (incorrectQuestions.length === 0) {
      setShowNoMistakesModal(true);
    }

    return incorrectQuestions;
  };

  const handleDownload = async () => {
    setShowDownloadCard(true);
    try {
      setTimeout(async () => {
        const canvas = await html2canvas(downloadCardRef.current, {
          scale: 2,
          backgroundColor: null,
          logging: true,
          useCORS: true,
          allowTaint: true,
          onclone: (document) => {
            // 클론된 문서의 차트 요소에 대한 추가 스타일링이 필요한 경우
            const chartElement = document.querySelector('.chart-container');
            if (chartElement) {
              chartElement.style.width = '200px';
              chartElement.style.height = '200px';
            }
          }
        });
        const image = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = image;
        link.download = `${nickname}-quiz-report.png`;
        link.click();
        setShowDownloadCard(false);
      }, 500); // 딜레이를 500ms로 증가
    } catch (error) {
      console.error('Error downloading report card:', error);
      setShowDownloadCard(false);
    }
  };

  const toggleAnswerSelection = (index) => {
    setSelectedAnswers(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  if (showNicknameModal) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full flex items-center justify-center p-4 animate-in fade-in duration-200">
        <Card className="w-full max-w-md shadow-2xl">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Smiley size={32} weight="duotone" className="text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 text-center">퀴즈에 참여하세요!</h2>
              <p className="text-sm text-muted-foreground mt-2 text-center">닉네임을 입력하고 시작해보세요</p>
            </div>
            <form onSubmit={handleNicknameSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nickname">닉네임</Label>
                <Input
                  id="nickname"
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="예: 홍길동"
                  className="text-center text-lg"
                  required
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full h-12 text-lg">
                <ArrowRight size={20} className="mr-2" weight="bold" />
                퀴즈 참여하기
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showResult) {
    return (
      <div className="container mx-auto px-4 pt-8 pb-24 bg-background min-h-screen">
        <div className="max-w-2xl mx-auto">
          <DisplayReportCard
            nickname={nickname}
            score={score}
            totalQuestions={questions.length}
            onDownload={handleDownload}
          />

          {/* 숨겨진 다운로드용 카드 */}
          {showDownloadCard && (
            <div className="fixed top-0 left-0 opacity-0 pointer-events-none">
              <div ref={downloadCardRef}>
                <DownloadReportCard
                  nickname={nickname}
                  score={score}
                  totalQuestions={questions.length}
                />
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-lg px-8 py-12">
            <h2 className="text-2xl font-bold mb-6 text-primary">🗒️ 퀴즈 풀이 상세</h2>

            <div className="space-y-4">
              {results.map((result, index) => (
                <div key={index} className={`p-4 rounded-lg ${result.isCorrect ? 'bg-green-100' : 'bg-red-100'}`}>
                  <p className="font-semibold">{index + 1}. {result.question}</p>
                  <p className={`mt-2 ${result.isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                    {result.isCorrect ? '정답' : '오답'}
                  </p>
                </div>
              ))}
            </div>
            <div className="flex flex-col space-y-4 mt-6">
              <Button
                onClick={restartQuiz}
                className=" w-full flex items-center justify-center h-[50px] text-base"
              >
                <ArrowClockwise className="mr-2" /> 다시 풀기
              </Button>
              {!isExtremeMode && (
                <Button
                  onClick={() => {
                    const incorrectQuestions = getIncorrectQuestions();
                    if (incorrectQuestions.length > 0) {
                      setShowReviewModal(true);
                    }
                  }}
                  className="bg-pink-500 hover:bg-pink-600 text-white py-2 px-4 rounded-xl w-full flex items-center justify-center h-[50px] text-base"
                >
                  <Book className="mr-2" /> 틀린 문제 복습
                </Button>
              )}
              <Button
                onClick={() => { setShowLeaderboard(true); fetchLeaderboard(); }}
                className=" w-full flex items-center justify-center h-[50px] text-base"
              >
                <Trophy className="mr-2" /> 리더보드 보기
              </Button>
            </div>
          </div>
        </div>
        {showLeaderboard && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
            <div className="bg-white p-8 rounded-lg w-full max-w-md max-h-[500px] flex flex-col">
              <h2 className="text-xl text-center font-bold mb-4">🏆 리더보드</h2>
              {leaderboard.length > 0 ? (
                <ul className="mb-4 overflow-y-auto flex-grow px-5">
                  {leaderboard.reduce((acc, entry, index, arr) => {
                    if (index === 0 || entry.score !== arr[index - 1].score) {
                      acc.rank = acc.rank + 1;
                    }

                    let icon = null;
                    if (acc.rank === 1) {
                      icon = <Trophy className="text-yellow-400" />;
                    } else if (acc.rank === 2) {
                      icon = <Medal className="text-gray-400" />;
                    } else if (acc.rank === 3) {
                      icon = <Medal className="text-orange-400" />;
                    }

                    acc.items.push(
                      <li key={entry.id} className="flex justify-between items-center py-2 border-b">
                        <span className="flex items-center">
                          {icon && <span className="mr-2">{icon}</span>}
                          <span className="font-semibold">{entry.nickname}</span>
                        </span>
                        <span>{entry.score} / {entry.totalQuestions}</span>
                      </li>
                    );

                    return acc;
                  }, { rank: 0, items: [] }).items}
                </ul>
              ) : (
                <div className="flex-grow flex items-center justify-center">
                  <p className="text-gray-500 py-6 text-base">퀴즈 참여 데이터가 없습니다.</p>
                </div>
              )}
              <div className="flex justify-between mt-4 gap-2">
                {isTeacher && (
                  <Button onClick={resetLeaderboard} variant="outline" className="flex-1">리더보드 초기화</Button>
                )}
                <Button onClick={() => setShowLeaderboard(false)} className="flex-1">닫기</Button>
              </div>
            </div>
          </div>
        )}
        {showNoMistakesModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
            <div className="bg-white p-5 rounded-lg w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">🎉 축하합니다!</h2>
              <p className="mb-4">틀린 문제가 없습니다.</p>
              <Button
                onClick={() => setShowNoMistakesModal(false)}
                className=" w-full"
              >
                확인
              </Button>
            </div>
          </div>
        )}
        {showReviewModal && getIncorrectQuestions().length > 0 && (
          <ReviewMistakes
            incorrectQuestions={getIncorrectQuestions()}
            onClose={() => setShowReviewModal(false)}
          />
        )}
      </div>
    );
  }

  if (showErrorModal) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
        <div className="bg-white p-5 rounded-lg w-full max-w-md">
          <h2 className="text-xl font-bold mb-4">오류</h2>
          <p className="mb-4">퀴즈가 없어 플레이 할 수 없습니다.</p>
          <Button
            onClick={() => navigate('/teacher-dashboard')}
            className=" w-full"
          >
            대시보드로 돌아가기
          </Button>
        </div>
      </div>
    );
  }

  if (showExtremeModeWarning) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
        <div className="bg-white p-5 rounded-lg w-full max-w-md">
          <h2 className="text-xl font-bold mb-4">익스트림 모드</h2>
          <p className="mb-4">익스트림 모드에서는 카드를 뒤집을 수 없습니다.</p>
          <Button
            onClick={() => setShowExtremeModeWarning(false)}
            className=" w-full"
          >
            확인
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-session-bg min-h-screen flex items-center justify-center px-3 sm:px-4 pt-16 sm:pt-20 pb-4 sm:pb-8">
      <div className="w-full max-w-2xl">
        <div className="bg-white bg-opacity-70 rounded-xl sm:rounded-2xl shadow-lg py-6 px-4 sm:py-12 sm:px-10 mb-4 sm:mb-8">
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <h2 className="text-lg sm:text-2xl font-bold text-primary">
              {currentQuestion + 1}번 문제 ({currentQuestion + 1}/{questions.length})
            </h2>
            <div className="flex items-center">
              <Heart className="text-red-500 mr-1 sm:mr-2" size={20} />
              <span className="text-base sm:text-lg font-semibold">{score}</span>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 sm:h-2.5 mb-4 sm:mb-10">
            <div className="bg-primary h-2 sm:h-2.5 rounded-full" style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}></div>
          </div>
          <div className="card-container mb-4 sm:mb-6 hover:shadow-lg relative whitespace-break-spaces" onClick={handleCardFlip} ref={cardRef}>
            {showConfetti && cardRef.current && (
              <ReactConfetti
                width={cardRef.current.offsetWidth}
                height={cardRef.current.offsetHeight}
                recycle={false}
                numberOfPieces={100}
                gravity={0.5}
                initialVelocityY={10}
                confettiSource={{
                  x: 0,
                  y: 0,
                  w: cardRef.current.offsetWidth,
                  h: 0
                }}
              />
            )}
            <div className={`card-inner ${isFlipped ? 'is-flipped' : ''}`}>
              <div className="card-front p-4 sm:p-8">
                <div className="text-lg sm:text-2xl font-bold text-center text-white">
                  {getCurrentQuestion()?.question}
                </div>
              </div>
              <div className="card-back p-4 sm:p-8">
                {getCurrentQuestion()?.isMultipleChoice ? (
                  <div className="space-y-2 sm:space-y-3">
                    <div className="text-base sm:text-xl font-bold text-center text-white mb-2 sm:mb-4">정답:</div>
                    {getCurrentQuestion()?.answerOptions.map((option, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg sm:rounded-xl ${getCurrentQuestion()?.correctAnswers.includes(idx)
                          ? 'bg-green-100 border-2 border-green-500'
                          : 'bg-gray-50/20'
                          }`}
                      >
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 rounded-full border-2 flex items-center justify-center ${getCurrentQuestion()?.correctAnswers.includes(idx)
                          ? 'border-green-500 bg-green-500'
                          : 'border-gray-300'
                          }`}>
                          {getCurrentQuestion()?.correctAnswers.includes(idx) && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className={`text-sm sm:text-lg ${getCurrentQuestion()?.correctAnswers.includes(idx)
                          ? 'text-green-800 font-medium'
                          : 'text-white'
                          }`}>
                          {option}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-lg sm:text-2xl font-bold text-center text-white">
                    {getCurrentQuestion()?.answer}
                  </div>
                )}
              </div>
            </div>
          </div>

          {!showNextButton && !isFlipped && (
            <div className="mt-4 sm:mt-6">
              {getCurrentQuestion()?.isMultipleChoice ? (
                <div>
                  <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
                    {getCurrentQuestion()?.answerOptions.map((option, index) => (
                      <div
                        key={index}
                        onClick={() => !isAnswered && toggleAnswerSelection(index)}
                        className={`flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg sm:rounded-xl cursor-pointer transition-all duration-200 ${selectedAnswers.includes(index)
                          ? 'bg-blue-100 border-2 border-blue-500'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                          }`}
                      >
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${selectedAnswers.includes(index)
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                          }`}>
                          {selectedAnswers.includes(index) && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className={`text-sm sm:text-lg ${selectedAnswers.includes(index)
                          ? 'text-blue-800 font-medium'
                          : 'text-gray-700'
                          }`}>
                          {option}
                        </span>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={handleSubmit}
                    disabled={selectedAnswers.length === 0 || isAnswered}
                    className="w-full bg-blue-600 text-white text-base sm:text-lg h-11 sm:h-[50px] py-2 sm:py-3 px-4 sm:px-6 rounded-xl hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                    정답 제출
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="정답을 입력하세요"
                    className="flex-1 px-3 sm:px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 text-base"
                    disabled={isAnswered}
                  />
                  <Button
                    onClick={handleSubmit}
                    className="h-11 sm:h-[50px] px-4 sm:px-6 text-base sm:text-lg rounded-xl"
                    disabled={isAnswered || !userAnswer.trim()}
                  >
                    <CheckCircle size={18} weight="bold" className="mr-1" />
                    제출
                  </Button>
                </div>
              )}
            </div>
          )}

          {feedback && (
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 mt-4">
              <div className={`p-3 px-4 sm:px-5 rounded-lg flex-1 ${feedback.isCorrect ? 'bg-green-100' : 'bg-red-100'}`}>
                <p className={`text-base sm:text-xl font-bold ${feedback.isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                  {feedback.isCorrect ? <CheckCircle className="inline mr-2" /> : <XIcon className="inline mr-2" />}
                  {feedback.message}
                </p>
              </div>
              {showNextButton && (
                <Button
                  onClick={nextQuestion}
                  className="flex items-center justify-center text-base sm:text-lg h-11 sm:h-[50px] px-4 sm:px-6 rounded-xl"
                >
                  다음 <ArrowRight className="ml-2" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      {showFlipModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
          <div className="bg-white p-5 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">😂 좀 더 생각해보기</h2>
            <p className="mb-4">점수를 획득할 수 없어요. 그래도 뒤집시겠습니까?</p>
            <div className="flex justify-end space-x-2">
              <Button onClick={() => setShowFlipModal(false)} className="">취소</Button>
              <Button onClick={confirmFlip} className="">확인</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QuizSession;
